import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import * as React from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { soilPD } from '../../../conversion/soil';
import { getLanguage } from '../../../localization';
import { cleanImage } from '../../../store/Image/imageSlice';
import { cleanAlert } from '../../../store/alert/alertSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectProject, setSoilInputData } from '../../../store/project/projectSlice';
import { cleanResult } from '../../../store/result/resultSlice';
import { selectSoil } from '../../../store/soil/soilSlice';
import { selectUser } from '../../../store/user/userSlice';
import { ERole, EStatus } from '../../enum';
import { ISoilInputData, TInputSoil } from '../../iProject';
import variable from '../../style/variable';
import { dialog, toastAlert } from '../../util';

interface IModalSoilProps {
    indexLayer: number;
    open: boolean;
    onClose: () => void;
}

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 900,
};

const soilId = 'soilId';
const soilPhysicalDataId = 'soilPhysicalDataId';

const ModalSoil = (props: IModalSoilProps) => {
    const project = useAppSelector(selectProject);
    const soil = useAppSelector(selectSoil);
    const user = useAppSelector(selectUser);

    const dispatch = useAppDispatch();

    const [soilIndex, setSoilInputDataIndex] = React.useState(-99);

    const { t } = useTranslation();

    const soilTable = soil
        .data!.filter(x => (user.data?.role != ERole.Standard ? x.status != EStatus.Deleted : x.status == EStatus.Public))
        .flatMap(x =>
            x
                .soilPhysicalData!.filter(t => t.unitOfMeasure == project.data?.projectInfo.unitOfMeasure!)
                .map(y => ({
                    soilId: x.id,
                    soilPhysicalDataId: y.id,
                    name: x.translations!.find(x => x.iso == getLanguage())?.name,
                    unitWeight: y.unitWeight,
                    saturatedUnitWeight: y.saturatedUnitWeight,
                    effectivePorosity: y.effectivePorosity,
                    drainedCohesion: y.drainedCohesion,
                    undrainedCohesion: y.undrainedCohesion,
                    frictionAngle: y.frictionAngle,
                    drainedSoilPermeability: y.drainedSoilPermeability,
                })),
        );

    const currentSoilTable = {
        soilId: project.data!.soilInputData!.soils![props.indexLayer].id,
        soilPhysicalDataId: project.data!.soilInputData!.soils![props.indexLayer].id,
        name: project.data!.soilInputData!.soils![props.indexLayer].translations!.find(x => x.iso == getLanguage())?.name,
        unitWeight: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].unitWeight,
        saturatedUnitWeight: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].saturatedUnitWeight,
        effectivePorosity: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].effectivePorosity,
        drainedCohesion: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].drainedCohesion,
        undrainedCohesion: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].undrainedCohesion,
        frictionAngle: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].frictionAngle,
        drainedSoilPermeability: project.data!.soilInputData!.soils![props.indexLayer].soilPhysicalData![0].drainedSoilPermeability,
    };

    React.useEffect(() => {
        if (props.open) {
            const ct = new Date(project.data?.soilInputData?.soils![props.indexLayer].lastUpdateDate!);
            const bt = new Date(soil.data!.find(x => x.id! == project.data!.soilInputData!.soils![props.indexLayer].baseId!)?.lastUpdateDate!);

            if (isAfter(bt, ct)) {
                dialog('warning', t('obsoleteTerrain', { terrain: currentSoilTable.name }));
            }
        }
    }, [props.open]);

    const handleClickApply = () => {
        let soilSelected = soilTable[soilIndex];
        let newSoil = soil.data!.find(x => soilSelected.soilId == x.id)!;
        newSoil = {
            ...newSoil,
            soilPhysicalData: newSoil.soilPhysicalData?.filter(x => soilSelected.soilPhysicalDataId == x.id)!,
        };
        let soils = [...project.data!.soilInputData!.soils!];
        let oldThickness = soils[props.indexLayer].soilPhysicalData![0].thickness;
        soils[props.indexLayer] = { ...newSoil, soilPhysicalData: [{ ...newSoil.soilPhysicalData![0], thickness: oldThickness }] };
        let soilInputData: ISoilInputData = {
            ...project.data?.soilInputData,
            soils: soils.map((x, i) => ({ ...x, layerNumber: i + 1 })),
        };
        dispatch(setSoilInputData({ ...project.data?.soilInputData, ...soilInputData }));
        toastAlert('success', t('updatedSoil'));
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());
    };

    const handleClose = () => {
        setSoilInputDataIndex(-99);
        props.onClose();
    };

    return (
        <Modal open={props.open} onClose={handleClose} aria-labelledby='modal-soil-title' aria-describedby='modal-soil-description'>
            <Box mx={style}>
                <Draggable cancel='.btn'>
                    <Stack variant='modal' height='100%' direction='column' spacing={4}>
                        <Stack variant='bottomLine' direction={'column'} spacing={2}>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography variant='h5' gutterBottom color={variable.greenLight}>
                                    {t('soilChoiceType', { layer: props.indexLayer + 1 })}
                                </Typography>
                                <IconButton aria-label='close-modal' onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                            <Stack>
                                <Typography gutterBottom>{t('currentSoilLayer')}</Typography>
                                <TableContainer className='btn'>
                                    <Table size='small' aria-label='current-soil-table'>
                                        <TableHead variant='greenLight2'>
                                            <TableRow>
                                                {Object.keys(soilTable[0]).map(
                                                    x =>
                                                        x != soilId &&
                                                        x != soilPhysicalDataId && (
                                                            <TableCell key={x} style={{ fontSize: '0.8rem', lineHeight: '1rem' }}>
                                                                {t(x, { unitOfMeasure: soilPD[x as TInputSoil]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                                            </TableCell>
                                                        ),
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                {Object.entries(currentSoilTable).map(
                                                    y =>
                                                        y[0] != soilId &&
                                                        y[0] != soilPhysicalDataId && (
                                                            <TableCell key={y[0]} align='center'>
                                                                {y[1]}
                                                            </TableCell>
                                                        ),
                                                )}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Stack>
                        </Stack>
                        <Stack flexGrow={1} spacing={2}>
                            <Stack flexGrow={1} position={'relative'}>
                                <Stack variant='scrollable'>
                                    <TableContainer className='btn'>
                                        <Table size='small' stickyHeader aria-label='soil-table'>
                                            <TableHead variant='green'>
                                                <TableRow>
                                                    {Object.keys(soilTable[0]).map(
                                                        x =>
                                                            x != soilId &&
                                                            x != soilPhysicalDataId && (
                                                                <TableCell key={x} style={{ fontSize: '0.8rem', lineHeight: '1rem' }}>
                                                                    {t(x, { unitOfMeasure: soilPD[x as TInputSoil]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                                                </TableCell>
                                                            ),
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {soilTable.map((x, i) => (
                                                    <TableRow key={x.name} hover variant={i == soilIndex ? 'greenSelected' : 'white'} onClick={() => setSoilInputDataIndex(i)}>
                                                        {Object.entries(x).map(
                                                            y =>
                                                                y[0] != soilId &&
                                                                y[0] != soilPhysicalDataId && (
                                                                    <TableCell key={y[0]} align='center'>
                                                                        {y[1]}
                                                                    </TableCell>
                                                                ),
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Stack>
                            </Stack>
                            <Stack direction={'row'} spacing={5} justifyContent={'flex-end'}>
                                <Button className='btn' variant={'white'} onClick={handleClose}>
                                    {t('close')}
                                </Button>
                                <Button className='btn' variant='orange' onClick={handleClickApply}>
                                    {t('apply')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Draggable>
            </Box>
        </Modal>
    );
};

export default ModalSoil;
